import React from 'react';
import cn from 'classnames';

import ArrowIosDown from '@crehana/icons/dist/outline/ArrowIosDown';
import { Collapse } from '@crehana/ui';

import { useTranslation } from '@crehana/i18n';

import type { FilterType } from 'Jsx/Catalog/types';
import { orderArrayByNumberAttribute } from 'Jsx/Catalog/utils';

import ButtonAnimation from '../../../ButtonAnimation';
import MenuIcon from '../../../MenuIcon';
import FilterSubItemContainer from '../FilterSubItemContainer';

import FilterItem from './FilterItem';

type FilterProps<T> = Readonly<{
  open: boolean;
  item: FilterType<T>;
  subItemsSelected: number[];
  mainTitle?: string;
  isMainTitle?: boolean;
  multipleSelection?: boolean;
  className?: string;
  hideMaxHeight?: boolean;
  enableHiddenOverflow?: boolean;
  reorderDisabledItems?: boolean;
  automaticScrollToSelected?: boolean;
  openFilter: () => void;
  toggleSubItem: (subItemId: number) => void;
}>;

function Filter<T>({
  // @ts-expect-error
  item: { id, name, subItems, url },
  open,
  openFilter: openFilterProp,
  toggleSubItem: toggleSubItemProp,
  subItemsSelected,
  mainTitle,
  className,
  isMainTitle = false,
  multipleSelection = false,
  hideMaxHeight = false,
  enableHiddenOverflow = false,
  reorderDisabledItems = true,
  automaticScrollToSelected = false,
}: FilterProps<T>) {
  const { t } = useTranslation();
  const aRef = React.useRef<HTMLAnchorElement>(null);
  const [expanded, setExpanded] = React.useState(false);
  const MAX_ITEMS = 5;

  const preventPropagation = (e: React.MouseEvent | React.KeyboardEvent) => {
    if (e.stopPropagation) e.stopPropagation();
  };

  const toggleExpanded = (newExpanded?: boolean) => {
    setExpanded(newExpanded || !expanded);
  };

  const toggleSubItem = (subItemId: number) => (e: React.MouseEvent) => {
    e.preventDefault();
    preventPropagation(e);
    toggleSubItemProp(subItemId);
  };

  /**
   * Expand filter if it has subItems selected and is not expanded
   */
  React.useEffect(() => {
    if (subItems) {
      const selectedIndex = Object.values(subItems).findIndex(subItem =>
        // @ts-expect-error
        subItemsSelected.includes(subItem.id),
      );

      if (selectedIndex >= MAX_ITEMS) {
        toggleExpanded(true);
      }
    }
  }, [subItems, subItemsSelected]);

  const subItemsArrOrderedByCountCourses = orderArrayByNumberAttribute(
    Object.values(subItems),
    'coursesNumber',
    'desc',
  );
  const subItemsArr = reorderDisabledItems
    ? subItemsArrOrderedByCountCourses.sort((a, b) => {
        if (!a.disabled && b.disabled) {
          return -1;
        }
        if (a.disabled && !b.disabled) {
          return 1;
        }
        return 0;
      })
    : subItemsArrOrderedByCountCourses;

  const renderSubItems = subItemsArr.map(
    // @ts-expect-error
    ({ id: ref, name: n, disabled, coursesNumber }, index) => {
      const isSelected = subItemsSelected.includes(ref);
      const hiddenItem = index >= MAX_ITEMS && !expanded;

      return (
        <li key={ref || index.toString()} hidden={hiddenItem}>
          <FilterItem
            multipleSelection={multipleSelection}
            isSelected={isSelected}
            onClick={toggleSubItem(ref)}
            disabled={disabled}
            name={n}
            tabIndex={open ? 0 : -1}
          />
        </li>
      );
    },
  );

  const openFilter = (e: React.MouseEvent) => {
    e.preventDefault();
    preventPropagation(e);

    openFilterProp();
  };

  const onOpenCallback = () => {
    if (automaticScrollToSelected) {
      setTimeout(() => {
        aRef.current?.focus();
        aRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }, 500);
    }
  };

  const renderTitle = () => {
    const title = isMainTitle && !!mainTitle ? mainTitle : name;

    return isMainTitle ? (
      <div className="flex items-center justify-between">
        <h2 className="tw-font-subtitle4 tw-font-semibold">
          {t(title, { defaultValue: title })}
        </h2>
        <ButtonAnimation type="button" opened={open}>
          <ArrowIosDown size={24} ignoreColor className="fill-current" />
        </ButtonAnimation>
      </div>
    ) : (
      <div className="flex items-center justify-start">
        <MenuIcon opened={open} />
        <h3 className="text-base-main pl-8 font-subtitle3 font-semibold">
          {t(title, { defaultValue: title })}
        </h3>
      </div>
    );
  };

  const renderExpansionButton = () => {
    const handleClick = (e: React.MouseEvent) => {
      e.preventDefault();
      preventPropagation(e);
      toggleExpanded();
    };

    const buttonLabel = expanded ? 'Ver menos' : 'Ver más';

    return (
      <button
        type="button"
        className="text-primary-main tw-font-semibold ml-8 sm:ml-16 mt-12 flex items-center"
        onClick={handleClick}
        aria-expanded={expanded}
      >
        <span className="pr-4">{buttonLabel}</span>
        <ButtonAnimation opened={!expanded} as="div">
          <ArrowIosDown
            size={16}
            ignoreColor
            className="fill-current min-w-16"
          />
        </ButtonAnimation>
      </button>
    );
  };

  const showButtonMoreItems = renderSubItems.length > MAX_ITEMS;

  return (
    <a
      ref={aRef}
      onClick={openFilter}
      href={url}
      className={cn('flex flex-col w-full cursor-pointer', className)}
      data-category-name={name}
      data-category-id={id}
    >
      {renderTitle()}

      <Collapse defaultOpen={open} open={open} onOpenCallback={onOpenCallback}>
        <FilterSubItemContainer
          hideMaxHeight={hideMaxHeight}
          noEndGradient
          noPaddingBottom
          noOverflowY
        >
          <ul className={enableHiddenOverflow ? 'sub-items--no-overflow' : ''}>
            {renderSubItems}
            {showButtonMoreItems && <li>{renderExpansionButton()}</li>}
          </ul>
        </FilterSubItemContainer>
      </Collapse>
    </a>
  );
}

export default Filter;
